import * as React from 'react';
import { useUID } from 'react-uid';
import { styled, useThemeProps } from '@mui/material/styles';
import MUISwitch, { SwitchProps as MUISwitchProps } from '@mui/material/Switch';
import Box, { BoxProps } from '@mui/system/Box';
import { FormHelperText, FormLabel } from '@mui/material';

const ContainerRootCheckbox = styled(Box)(
  ({ theme }) => `
  font-size: 0;
  position: relative;
  display: inline-flex;
  cursor: pointer;
  align-items: center;
  & .FSSwitch-label{
    margin-left: 10px;
  }
  &.checked {
    .FSSwitch-label {
      color: ${theme.palette.primary.main};
    }
  }
}`
);

const StyledSwitch = styled(MUISwitch)(({ theme }) => ({
  width: 40,
  height: 24,
  padding: 0,
  margin: 10,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 4,
    transitionDuration: '120ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundImage: theme.palette.gradientColor.short,
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      backgroundColor: theme.palette.grey[500],
      boxShadow: '0 0 1px 8px rgba(0, 0, 0, 0.25)',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      opacity: 0.4,
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.4,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 16,
    height: 16,
    backgroundColor: '#fff',
  },
  '& .MuiSwitch-track': {
    borderRadius: 16,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[600]
        : theme.palette.grey[400],
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 120,
    }),
  },
}));

export interface SwitchProps extends Omit<MUISwitchProps, 'ref'> {
  id?: string;
  name?: string;
  label?: string | null;
  sx?: BoxProps['sx'];
  /** not supported yet */
  error?: boolean;
  helperText?: string;
}

const Switch = React.forwardRef(function FSSwitch(
  inProps: SwitchProps,
  ref: React.ForwardedRef<HTMLButtonElement>
) {
  const props = useThemeProps({ props: inProps, name: 'FSSwitch' });
  const uniqueId = useUID();
  const id = props.id || `FSSwitch-${uniqueId}`;
  const { label, helperText, error, sx, ...switchProps } = props;

  return (
    <Box sx={sx}>
      <ContainerRootCheckbox className={props.checked ? 'checked' : ''}>
        <StyledSwitch {...switchProps} id={id} inputRef={ref} />
        {label && (
          <FormLabel
            htmlFor={id}
            className={error ? 'error FSSwitch-label' : 'FSSwitch-label'}
            required={props.required}
          >
            {label}
          </FormLabel>
        )}
      </ContainerRootCheckbox>
      {helperText && (
        <FormHelperText
          color={error ? 'error' : undefined}
          sx={{ lineHeight: '2rem' }}
        >
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
});

export default Switch;
